import {
    collection,
    doc,
    getDoc,
    addDoc,
    updateDoc,
    query,
    where,
    getDocs,
    getCountFromServer,
    orderBy,
    startAfter,
    limit,
    Timestamp,
    Firestore,
    QueryConstraint,
    QueryLimitConstraint,
    QueryOrderByConstraint,
    QueryStartAtConstraint,
    documentId
} from 'firebase/firestore';
import { User } from 'firebase/auth';

// Types
interface UserData {
    id: string;
    companyId?: string;
    // Add other user fields here
    [key: string]: any; // For other user fields
}

// common interface for all settings
interface Settings {
    id: string;
    value: string;
    isActive: boolean;
}

interface SettingsRequest {
    id?: string;
    value: string;
    discription?: string;
    level?: number;
    priority?: number;
    color?: string;
    departmentFor?: string;
    selectedFilterIds?: string[];
    registrationFeeDetails?: {
        registrationFeeName: string;
        registrationFeeAmount: string
    }
    documentCategory?: {
        categoryName: string;
        documentValues: string[];
    }
    tabPermissions?:
    {
        name: string;
        read: boolean;
        write: boolean;
    }[];
    actionPermission?: {
        name: string;
        allowed: boolean;
    }[];
    bulkActionPermission?: {
        name: string;
        id: number;
        active: boolean;
        view: boolean;
    }[];
    sidebarNavigation?: {
        id: number;
        name: string;
        normalIcon: string;
        activeIcon: string;
        link: string | object[]
        isActive: boolean;
        isVisible: boolean;
    }[];
    pipeline?: {
        checkpointName: string;
        checkpointDescription: string;
        checkpointOrder: number;
        criteria: {
            criteriaName: string;
            criteriaValue: {
                id: string;
                name: string;
            }[]
        }[];
    }[];
    filterButton?: {
        sourceIds: string[],
        stageIds: string[],
        stateIds: string[],
        statusIds: string[],
        admissionStatusIds: string[],
        welcomeCallStatusIds: string[],
        visaProcessingStatusIds: string[],
        applicationStatusIds: string[],
        conditionalOfferLetterStatusIds: string[],
        unconditionalOfferLetterStatusIds: string[],
        feePaymentStatusIds: string[],
        fileSubmissionStatusIds: string[],
        preferredStreamIds: string[],
        assigneeIds: string[],
        subAgentIds: string[],
        superAgentIds: string[],
        followUpIds: string[],
        campaignIds: string[],
        destinationIds: string[],
        followUpStatus: string[],
    };
}

export interface SettingsResponse {
    success: boolean;
    message?: string;
    data: {
        id: string;
        campaign?: string;
        source?: string;
        stage?: string;
        state?: string;
        status?: string;
        admissionStatus?: string;
        welcomeCallByAdmissionTeamStatus?: string;
        visaFileProcessingStatus?: string;
        applicationMovedStatus?: string
        offerLetterStatus?: string;
        feePaymentStatus?: string;
        fileSubmissionStatus?: string;
        department?: string;
        prefferedStream?: string;
        programType?: string;
        superAgent?: string;
        pipeline?: string;
        filterButton?: string;
        registrationFeeDetails?: {
            registrationFeeName: string;
            registrationFeeAmount: string;
        };
        documentCategory?: {
            categoryName: string;
            documentValues: string[];
        };
    };
}

// Function to add a new campaign to the database related to the companyId ie companies/{companyId}/leadCapaigns
export async function addCampaign(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Campaign name is required');
        }

        // Add new campaign
        const campaignDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadCampaigns`), {
            campaign: request?.value.toLocaleUpperCase(),
            isActive: true
        });

        console.log('campaignDoc:', campaignDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: campaignDoc.id,
                campaign: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding campaign';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addLeadSource(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Source name is required');
        }

        // Add new source
        const sourceDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadSources`), {
            source: request?.value,
        });

        console.log('sourceDoc:', sourceDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: sourceDoc.id,
                source: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding source';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addLeadSubSource(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value || !request?.id) {
            throw new Error('Sub source name and source ID are required');
        }

        // Add new sub source
        const subSourceDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadSubSources`), {
            subSource: request?.value,
            sourceId: request?.id,
        });

        console.log('subSourceDoc:', subSourceDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: subSourceDoc.id,
                source: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding sub source';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addLeadStage(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value || !request?.priority || !request?.color) {
            throw new Error('Stage, priority and color are required');
        }

        // Add new stage
        const stageDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadStages`), {
            stage: request?.value,
            priority: request?.priority,
            color: request?.color.toLocaleUpperCase(),
        });

        console.log('stageDoc:', stageDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: stageDoc.id,
                stage: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding stage';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addLeadSubStage(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value || !request?.id) {
            throw new Error('Sub stage name and stage ID are required');
        }

        // Add new sub stage
        const subStageDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadSubStages`), {
            subStage: request?.value,
            stageId: request?.id,
        });

        console.log('subStageDoc:', subStageDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: subStageDoc.id,
                stage: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding sub stage';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addLeadStates(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value || !request?.color) {
            throw new Error('State and color are required');
        }

        // Add new state
        const stateDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadStates`), {
            state: request?.value,
            color: request?.color.toLocaleUpperCase(),
        });

        console.log('stateDoc:', stateDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: stateDoc.id,
                state: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding state';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addLeadStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value || !request?.priority || !request?.color) {
            throw new Error('Status, priority and color are required');
        }

        // Add new status
        const statusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadStatuses`), {
            status: request?.value,
            priority: request?.priority,
            color: request?.color.toLocaleUpperCase(),
        });

        console.log('statusDoc:', statusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: statusDoc.id,
                status: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding status';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addLeadSubStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can add lead sub status');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value || !request?.id) {
            throw new Error('Sub status name and status ID are required');
        }

        // Add new sub status
        const subStatusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadSubStatuses`), {
            subStatus: request?.value,
            statusId: request?.id,
        });

        console.log('subStatusDoc:', subStatusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: subStatusDoc.id,
                status: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding sub status';
        console.error('Error in addLeadSubStatus:', error);
        throw new Error(errorMessage);
    }
}

export async function addAdmissionStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value || !request?.level) {
            throw new Error('Admission status and level are required');
        }

        // Add new source
        const admissionStatusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadAdmissionStatus`), {
            admissionStatus: request?.value,
            level: request?.level,
        });

        console.log('admissionStatusDoc:', admissionStatusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: admissionStatusDoc.id,
                admissionStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding admissionStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addLeadSubAdmissionStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value || !request?.id) {
            throw new Error('Sub admission status, admission status ID and level are required');
        }

        // Add new sub admission status
        const subAdmissionStatusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadAdmissionSubStatus`), {
            subAdmissionStatus: request?.value,
            admissionStatusId: request?.id,
        });

        console.log('subAdmissionStatusDoc:', subAdmissionStatusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: subAdmissionStatusDoc.id,
                admissionStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding subAdmissionStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addWelcomeCallStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Welcome call status name is required');
        }

        // Add new source
        const welcomeCallByAdmissionTeamStatusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadWelcomeCallByAdmissionTeamStatus`), {
            welcomeCallByAdmissionTeamStatus: request?.value,
        });

        console.log('welcomeCallByAdmissionTeamStatusDoc:', welcomeCallByAdmissionTeamStatusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: welcomeCallByAdmissionTeamStatusDoc.id,
                welcomeCallByAdmissionTeamStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding welcomeCallByAdmissionTeamStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addVisaProcessingStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Visa processing status name is required');
        }

        // Add new source
        const visaFileProcessingStatusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadVisaFileProcessingStatus`), {
            visaFileProcessingStatus: request?.value,
        });

        console.log('visaFileProcessingStatusDoc:', visaFileProcessingStatusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: visaFileProcessingStatusDoc.id,
                visaFileProcessingStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding visaFileProcessingStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addApplicationStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Application status name is required');
        }

        // Add new source
        const applicationMovedStatusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadApplicationMovedStatus`), {
            applicationMovedStatus: request?.value,
        });

        console.log('applicationMovedStatusDoc:', applicationMovedStatusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: applicationMovedStatusDoc.id,
                applicationMovedStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding applicationMovedStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addOfferLetterStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Offer letter status name is required');
        }

        // Add new source
        const offerLetterStatusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadOfferLetterStatus`), {
            offerLetterStatus: request?.value,
        });

        console.log('offerLetterStatusDoc:', offerLetterStatusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: offerLetterStatusDoc.id,
                offerLetterStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding offerLetterStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addFeePaymentStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Fee payment status name is required');
        }

        // Add new source
        const feePaymentStatusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadFeePaymentStatus`), {
            feePaymentStatus: request?.value,
        });

        console.log('feePaymentStatusDoc:', feePaymentStatusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: feePaymentStatusDoc.id,
                feePaymentStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding feePaymentStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addFileSubmissionStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('File Submission status name is required');
        }

        // Add new source
        const fileSubmissionStatusDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadFileSubmissionStatus`), {
            fileSubmissionStatus: request?.value,
        });

        console.log('fileSubmissionStatusDoc:', fileSubmissionStatusDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: fileSubmissionStatusDoc.id,
                fileSubmissionStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding fileSubmissionStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addDepartment(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Department name is required');
        }

        // Add new source
        const departmentDoc = await addDoc(collection(db, `companies/${userData.companyId}/${request?.departmentFor}`), {
            department: request?.value,
            selectedFilterIds: request?.selectedFilterIds || [],
            tabPermissions: request?.tabPermissions,
            actionPermission: request?.actionPermission,
            bulkActionPermission: request?.bulkActionPermission ?? [],
            sidebarNavigation: request?.sidebarNavigation ?? [],
        });

        console.log('departmentDoc:', departmentDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: departmentDoc.id,
                department: request.value
            }
        };

    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding department';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addPrefferedStreams(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Stream name is required');
        }

        // Add new source
        const streamDoc = await addDoc(collection(db, `companies/${userData.companyId}/preferredStreams`), {
            preferredStream: request?.value,
        });

        console.log('streamDoc:', streamDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: streamDoc.id,
                prefferedStream: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding stream';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addProgramTypes(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Program type name is required');
        }

        // Add new source
        const programTypeDoc = await addDoc(collection(db, `companies/${userData.companyId}/programTypes`), {
            programType: request?.value,
        });

        console.log('programTypeDoc:', programTypeDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: programTypeDoc.id,
                programType: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding program type';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addSuperAgents(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Super agent name is required');
        }

        // Add new super agent
        const superAgentDoc = await addDoc(collection(db, `companies/${userData.companyId}/superAgents`), {
            superAgent: request?.value,
            isActive: true
        });

        console.log('superAgentDoc:', superAgentDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: superAgentDoc.id,
                superAgent: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding super agent';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addPipeline(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {

        console.log('request:', request);

        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }
        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }
        if (!request?.value) {
            throw new Error('Pipeline name is required');
        }

        if (!request?.pipeline || request?.pipeline.length === 0) {
            throw new Error('Pipeline checkpoints are required');
        }

        // Add new pipeline
        const pipelineDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadPipelines`), {
            pipelineName: request?.value,
            pipelineDescription: request?.discription ?? '',
            checkpoints: request?.pipeline ?? [],
        });

        console.log('pipelineDoc:', pipelineDoc);
        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: pipelineDoc.id,
                pipeline: request.value,
            }
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding pipeline';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function addFilterButton(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Filter button name is required');
        }

        // Add new filter button
        const filterButtonDoc = await addDoc(collection(db, `companies/${userData.companyId}/filterButtons`), {
            name: request?.value,
            description: request?.discription ?? '',
            color: request?.color?.toLocaleUpperCase(),
            filterButton: request?.filterButton ?? [],
        });

        console.log('filterButtonDoc:', filterButtonDoc);

        return {
            success: true,
            message: `${request.value} added successfully`,
            data: {
                id: filterButtonDoc.id,
                filterButton: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding filter button';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

// registrationFeeDetails
export async function addRegistrationFeeDetails(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.registrationFeeDetails) {
            throw new Error('Registration fee details are required');
        }

        // Add new registration fee details
        const registrationFeeDetailsDoc = await addDoc(
            collection(db, `companies/${userData.companyId}/registrationFeeDetails`),
            {
                registrationFeeName: request.registrationFeeDetails.registrationFeeName,
                registrationFeeAmount: request.registrationFeeDetails.registrationFeeAmount,
            }
        );

        return {
            success: true,
            message: 'Registration fee details added successfully',
            data: {
                id: registrationFeeDetailsDoc.id,
                registrationFeeDetails: request.registrationFeeDetails
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding registration fee details';
        console.error('Error in addRegistrationFeeDetails:', error);
        throw new Error(errorMessage);
    }
}
// Function to update a campaign in the database related to the companyId ie companies/{companyId}/leadCapaigns
export async function updateCampaign(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Campaign name is required');
        }

        // Update campaign
        const campaignDoc = doc(db, `companies/${userData.companyId}/leadCampaigns`, id);
        await updateDoc(campaignDoc, {
            campaign: request?.value.toLocaleUpperCase(),
            isActive: true
        });

        console.log('campaignDoc:', campaignDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: campaignDoc.id,
                campaign: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating campaign';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateLeadSource(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Source name is required');
        }

        // Update source
        const sourceDoc = doc(db, `companies/${userData.companyId}/leadSources`, id);
        await updateDoc(sourceDoc, {
            source: request?.value,
        });

        console.log('sourceDoc:', sourceDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: sourceDoc.id,
                source: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating source';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateLeadSubSource(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Sub source name and source ID are required');
        }

        // Update sub source
        const subSourceDoc = doc(db, `companies/${userData.companyId}/leadSubSources`, id);
        await updateDoc(subSourceDoc, {
            subSource: request?.value,
        });

        console.log('subSourceDoc:', subSourceDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: subSourceDoc.id,
                source: request.value
            }
        };

    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating sub source';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateLeadStage(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Stage name is required');
        }

        // Update stage
        const stageDoc = doc(db, `companies/${userData.companyId}/leadStages`, id);
        await updateDoc(stageDoc, {
            stage: request?.value,
            priority: request?.priority,
            color: request?.color?.toLocaleUpperCase(),
        });
        console.log('stageDoc:', stageDoc);
        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: stageDoc.id,
                stage: request.value
            }
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating stage';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateLeadSubStage(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;
        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }
        if (!request?.value) {
            throw new Error('Sub stage name and stage ID are required');
        }
        // Update sub stage
        const subStageDoc = doc(db, `companies/${userData.companyId}/leadSubStages`, id);
        await updateDoc(subStageDoc, {
            subStage: request?.value
        });
        console.log('subStageDoc:', subStageDoc);
        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: subStageDoc.id,
                stage: request.value
            }
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating sub stage';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateLeadStates(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('State name is required');
        }

        // Update state
        const stateDoc = doc(db, `companies/${userData.companyId}/leadStates`, id);
        await updateDoc(stateDoc, {
            state: request?.value,
            color: request?.color?.toLocaleUpperCase(),
        });

        console.log('stateDoc:', stateDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: stateDoc.id,
                state: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating state';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateLeadStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Status name is required');
        }

        // Update status
        const statusDoc = doc(db, `companies/${userData.companyId}/leadStatuses`, id);
        await updateDoc(statusDoc, {
            status: request?.value,
            priority: request?.priority,
            color: request?.color?.toLocaleUpperCase(),
        });

        console.log('statusDoc:', statusDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: statusDoc.id,
                status: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating status';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateLeadSubStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can update lead sub status');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;
        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }
        if (!request?.value) {
            throw new Error('Sub status name is required');
        }

        // Update sub status
        const subStatusDoc = doc(db, `companies/${userData.companyId}/leadSubStatuses`, id);
        await updateDoc(subStatusDoc, {
            subStatus: request?.value
        });
        console.log('subStatusDoc:', subStatusDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: subStatusDoc.id,
                status: request.value
            }
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating sub status';
        console.error('Error in updateLeadSubStatus:', error);
        throw new Error(errorMessage);
    }
}

export async function updateAdmissionStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Admission status name is required');
        }

        // Update source
        const admissionStatusDoc = doc(db, `companies/${userData.companyId}/leadAdmissionStatus`, id);
        await updateDoc(admissionStatusDoc, {
            admissionStatus: request?.value,
            level: request?.level,
        });

        console.log('admissionStatusDoc:', admissionStatusDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: admissionStatusDoc.id,
                admissionStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating admissionStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateLeadSubAdmissionStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }
        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;
        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }
        if (!request?.value) {
            throw new Error('Sub admission status and admission status ID are required');
        }
        // Update sub admission status
        const subAdmissionStatusDoc = doc(db, `companies/${userData.companyId}/leadAdmissionSubStatus`, id);
        await updateDoc(subAdmissionStatusDoc, {
            subAdmissionStatus: request?.value,
        });
        console.log('subAdmissionStatusDoc:', subAdmissionStatusDoc);
        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: subAdmissionStatusDoc.id,
                admissionStatus: request.value
            }
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating subAdmissionStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateWelcomeCallStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Welcome call status name is required');
        }

        // Update source
        const welcomeCallByAdmissionTeamStatusDoc = doc(db, `companies/${userData.companyId}/leadWelcomeCallByAdmissionTeamStatus`, id);
        await updateDoc(welcomeCallByAdmissionTeamStatusDoc, {
            welcomeCallByAdmissionTeamStatus: request?.value,
        });

        console.log('welcomeCallByAdmissionTeamStatusDoc:', welcomeCallByAdmissionTeamStatusDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: welcomeCallByAdmissionTeamStatusDoc.id,
                welcomeCallByAdmissionTeamStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating welcomeCallByAdmissionTeamStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateVisaProcessingStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Visa processing status name is required');
        }

        // Update source
        const visaFileProcessingStatusDoc = doc(db, `companies/${userData.companyId}/leadVisaFileProcessingStatus`, id);
        await updateDoc(visaFileProcessingStatusDoc, {
            visaFileProcessingStatus: request?.value,
        });

        console.log('visaFileProcessingStatusDoc:', visaFileProcessingStatusDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: visaFileProcessingStatusDoc.id,
                visaFileProcessingStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating visaFileProcessingStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateApplicationStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Application status name is required');
        }

        // Update source
        const applicationMovedStatusDoc = doc(db, `companies/${userData.companyId}/leadApplicationMovedStatus`, id);
        await updateDoc(applicationMovedStatusDoc, {
            applicationMovedStatus: request?.value,
        });

        console.log('applicationMovedStatusDoc:', applicationMovedStatusDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: applicationMovedStatusDoc.id,
                applicationMovedStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating applicationMovedStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateOfferLetterStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Offer letter status name is required');
        }

        // Update source
        const offerLetterStatusDoc = doc(db, `companies/${userData.companyId}/leadOfferLetterStatus`, id);
        await updateDoc(offerLetterStatusDoc, {
            offerLetterStatus: request?.value,
        });

        console.log('offerLetterStatusDoc:', offerLetterStatusDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: offerLetterStatusDoc.id,
                offerLetterStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating offerLetterStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateFeePaymentStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Fee payment status name is required');
        }

        // Update source
        const feePaymentStatusDoc = doc(db, `companies/${userData.companyId}/leadFeePaymentStatus`, id);
        await updateDoc(feePaymentStatusDoc, {
            feePaymentStatus: request?.value,
        });

        console.log('feePaymentStatusDoc:', feePaymentStatusDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: feePaymentStatusDoc.id,
                feePaymentStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating feePaymentStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateFileSubmissionStatus(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('File Submission status name is required');
        }

        // Update source
        const fileSubmissionStatusDoc = doc(db, `companies/${userData.companyId}/leadFileSubmissionStatus`, id);
        await updateDoc(fileSubmissionStatusDoc, {
            fileSubmissionStatus: request?.value,
        });

        console.log('fileSubmissionStatusDoc:', fileSubmissionStatusDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: fileSubmissionStatusDoc.id,
                fileSubmissionStatus: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating fileSubmissionStatus';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateDepartment(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication 
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Department name is required');
        }

        // Update source
        const departmentDoc = doc(db, `companies/${userData.companyId}/${request?.departmentFor}`, id);
        await updateDoc(departmentDoc, {
            department: request?.value,
            selectedFilterIds: request?.selectedFilterIds || [],
            tabPermissions: request?.tabPermissions,
            actionPermission: request?.actionPermission,
            ...(request?.bulkActionPermission && { bulkActionPermission: request.bulkActionPermission }),
            ...(request?.sidebarNavigation && { sidebarNavigation: request.sidebarNavigation }),
        });

        console.log('departmentDoc:', departmentDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: departmentDoc.id,
                department: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating department';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updatePrefferedStreams(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Stream name is required');
        }

        // Update source
        const streamDoc = doc(db, `companies/${userData.companyId}/preferredStreams`, id);
        await updateDoc(streamDoc, {
            preferredStream: request?.value,
        });

        console.log('streamDoc:', streamDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: streamDoc.id,
                prefferedStream: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating stream';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateProgramTypes(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Program type name is required');
        }

        // Update source
        const programTypeDoc = doc(db, `companies/${userData.companyId}/programTypes`, id);
        await updateDoc(programTypeDoc, {
            programType: request?.value,
        });

        console.log('programTypeDoc:', programTypeDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: programTypeDoc.id,
                programType: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating program type';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateSuperAgents(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Super agent name is required');
        }

        // Update super agent
        const superAgentDoc = doc(db, `companies/${userData.companyId}/superAgents`, id);
        await updateDoc(superAgentDoc, {
            superAgent: request?.value,
            isActive: true
        });

        console.log('superAgentDoc:', superAgentDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: superAgentDoc.id,
                superAgent: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating super agent';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updatePipeline(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Pipeline name is required');
        }

        if (!request?.pipeline || request?.pipeline.length === 0) {
            throw new Error('Pipeline checkpoints are required');
        }

        // Update pipeline
        const pipelineDoc = doc(db, `companies/${userData.companyId}/leadPipelines`, id);
        await updateDoc(pipelineDoc, {
            pipelineName: request?.value,
            pipelineDescription: request?.discription ?? '',
            checkpoints: request?.pipeline ?? [],
        });

        console.log('pipelineDoc:', pipelineDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: pipelineDoc.id,
                pipeline: request.value,
            }
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating pipeline';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

export async function updateFilterButton(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        if (!request?.value) {
            throw new Error('Filter button name is required');
        }

        // Update filter button
        const filterButtonDoc = doc(db, `companies/${userData.companyId}/filterButtons`, id);
        await updateDoc(filterButtonDoc, {
            name: request?.value,
            description: request?.discription ?? '',
            color: request?.color?.toLocaleUpperCase(),
            filterButton: request?.filterButton ?? [],
        });

        console.log('filterButtonDoc:', filterButtonDoc);

        return {
            success: true,
            message: `${request.value} updated successfully`,
            data: {
                id: filterButtonDoc.id,
                filterButton: request.value
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating filter button';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

// updateRegistrationFeeDetails
export async function updateRegistrationFeeDetails(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        if (!user) {
            throw new Error('Only authenticated users can update company data');
        }

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        const feeDetails = request?.registrationFeeDetails;
        if (!feeDetails || !feeDetails.registrationFeeName || feeDetails.registrationFeeAmount == null) {
            throw new Error('Registration fee name and amount are required');
        }

        const registrationFeeDoc = doc(db, `companies/${userData.companyId}/registrationFeeDetails`, id);

        await updateDoc(registrationFeeDoc, {
            registrationFeeName: feeDetails.registrationFeeName,
            registrationFeeAmount: feeDetails.registrationFeeAmount,
        });

        console.log('Updated registrationFeeDoc:', registrationFeeDoc);

        return {
            success: true,
            message: `${feeDetails.registrationFeeName} updated successfully`,
            data: {
                id: registrationFeeDoc.id,
                registrationFeeDetails: feeDetails
            }
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating registration fee details';
        console.error('Error in updateRegistrationFeeDetails:', error);
        throw new Error(errorMessage);
    }
}

// Document Category functions
export async function addDocumentCategory(
    db: Firestore,
    user: User | null,
    request: SettingsRequest
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('User not authenticated');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('User company ID not found');
        }

        if (!request?.documentCategory?.categoryName) {
            throw new Error('Category name is required');
        }

        // Add new document category
        const categoryDoc = await addDoc(collection(db, `companies/${userData.companyId}/leadDocumentCategories`), {
            categoryName: request.documentCategory.categoryName,
            documentValues: request.documentCategory.documentValues || []
        });

        console.log('Document category created:', categoryDoc);

        return {
            success: true,
            message: `${request.documentCategory.categoryName} added successfully`,
            data: {
                id: categoryDoc.id,
                documentCategory: request.documentCategory
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding document category';
        console.error('Error in addDocumentCategory:', error);
        throw new Error(errorMessage);
    }
}

export async function updateDocumentCategory(
    db: Firestore,
    user: User | null,
    request: SettingsRequest,
    id: string
): Promise<SettingsResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('User not authenticated');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('User company ID not found');
        }

        if (!request?.documentCategory?.categoryName) {
            throw new Error('Category name is required');
        }

        // Update document category
        await updateDoc(doc(db, `companies/${userData.companyId}/leadDocumentCategories/${id}`), {
            categoryName: request.documentCategory.categoryName,
            documentValues: request.documentCategory.documentValues || []
        });

        return {
            success: true,
            message: `${request.documentCategory.categoryName} updated successfully`,
            data: {
                id: id,
                documentCategory: request.documentCategory
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating document category';
        console.error('Error in updateDocumentCategory:', error);
        throw new Error(errorMessage);
    }
}
